import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Building07Icon } from '@untitledui-icons/react/line'
import { AsyncMultiSelect, type SelectOption } from '@/components/forms/AsyncMultiSelect'
import type { AssistantListFilters } from '@/types/assistant'
import { usePermissions, useCustomersByIds } from '@/hooks/api'

interface AssistantFiltersProps {
  filters: AssistantListFilters
  onFiltersChange: (filters: AssistantListFilters) => void
}

export function AssistantFilters({ filters, onFiltersChange }: AssistantFiltersProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [selectedCustomers, setSelectedCustomers] = useState<SelectOption[]>([])

  const canViewCustomers = can('view:customers')

  const { data: existingCustomers = [] } = useCustomersByIds(
    filters.customer_id || [],
    (filters.customer_id?.length || 0) > 0 && canViewCustomers
  )

  const existingCustomerIds = existingCustomers.map((c) => c.id).join(',')
  const filterCustomerIds = (filters.customer_id || []).join(',')

  useEffect(() => {
    if (existingCustomers.length > 0) {
      setSelectedCustomers(existingCustomers.map((c) => ({ id: c.id, name: c.name })))
    } else if (!filters.customer_id?.length) {
      setSelectedCustomers([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCustomerIds, filterCustomerIds])

  const handleCustomersChange = useCallback(
    (customers: SelectOption[]) => {
      setSelectedCustomers(customers)
      if (customers.length > 0) {
        onFiltersChange({ ...filters, customer_id: customers.map((c) => c.id) })
      } else {
        const { customer_id: _, ...rest } = filters
        onFiltersChange(rest as AssistantListFilters)
      }
    },
    [filters, onFiltersChange]
  )

  if (!canViewCustomers) return null

  return (
    <AsyncMultiSelect
      placeholder={t('resources.conversations.filters.customer')}
      searchPlaceholder={t('resources.generic.search')}
      searchEndpoint="/customers"
      value={selectedCustomers}
      onChange={handleCustomersChange}
      emptyMessage={t('resources.dashboard.filters.no_results')}
      icon={Building07Icon}
      className="w-40 sm:w-56"
    />
  )
}
